import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { AlertService } from '@dc-core/dc-services/alert.service';
import { LaneApiService, UpsertVenueGameActivityRequest } from '@venue/openapi';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { LocalGameUser, NewLocalGameStateService } from 'src/app/modules/games/services/new-local-game-state.service';
import { dateTimeFormat } from 'src/app/modules/games/types/game-settings.definitions';
import { LocalVenuePartyGamesDefinition } from 'src/app/modules/local-games/data/local-venue-party-games.definitions';
import { DartCounterSmartDevice } from 'src/app/modules/smart-devices/models/smart-devices.models';
import { SmartDevicesStateService } from 'src/app/modules/smart-devices/services/smart-devices-state.service';
import { VenueLaneSessionService } from 'src/app/services/venues/venue-lane-session.service';
import {
    IncomingGameResultDeepLinkPayload,
    OutgoingTargetArcadeDeepLinkPayload,
} from 'src/app/shared/deep-links/deep-link-payloads';
import { DeepLinkService } from 'src/app/shared/services/deep-link.service';
import { generateUuid } from 'src/app/shared/utils/uuid.utils';

export type TargetArcadeDeepLinkTestDialogPayload = Record<string, never>;

interface PendingTargetArcadeGame {
    game: string;
    sessionId: string;
    startedAt: string;
}

@Injectable({
    providedIn: 'root',
})
export class VenueDeeplinkTargetArcadeService {
    private deepLinkService: DeepLinkService = inject(DeepLinkService);
    private venueLaneSessionService: VenueLaneSessionService = inject(VenueLaneSessionService);
    private newLocalGameStateService: NewLocalGameStateService = inject(NewLocalGameStateService);
    private smartDeviceStateService: SmartDevicesStateService = inject(SmartDevicesStateService);
    private laneApiService: LaneApiService = inject(LaneApiService);
    private alertService: AlertService = inject(AlertService);

    public pendingGame: WritableSignal<PendingTargetArcadeGame | null> = signal(null);
    public sessionId: Signal<string | null> = this.venueLaneSessionService.currentSessionId;
    public players: Signal<LocalGameUser[] | null> = computed(() => this.newLocalGameStateService.users());
    public playerCount: Signal<number> = computed(() => this.players().length ?? 0);
    public playerNames: Signal<string[]> = computed(() =>
        this.newLocalGameStateService
            .users()
            .map((user) => user.full_name)
            .filter(Boolean)
    );
    public registeredOmni: Signal<DartCounterSmartDevice | null> = computed(
        () => this.smartDeviceStateService.currentRegisteredOmnis()[0] ?? null
    );

    public registeredOmniIp: Signal<string | null> = computed(() => this.registeredOmni()?.ip_address ?? null);

    public startGame(game: LocalVenuePartyGamesDefinition): void {
        if (!game) return;
        const gameName = game.slug;

        if (!this.sessionId()) {
            return;
        }

        const sessionId = this.sessionId();
        const playerNames = this.playerNames().join(',');
        const omniIp = this.registeredOmniIp();

        this.pendingGame.set({
            game: gameName,
            sessionId: sessionId,
            startedAt: moment().utc().format(dateTimeFormat),
        });

        const payload: OutgoingTargetArcadeDeepLinkPayload = {
            startgame: gameName,
            players: playerNames,
            session_id: sessionId,
            connectomni: omniIp ?? null,
        };

        this.openTargetArcade(payload);
    }

    public handleGame(payload: IncomingGameResultDeepLinkPayload): void {
        if (!payload) {
            return;
        }

        const pendingGame = this.pendingGame();

        if (!pendingGame) {
            return;
        }

        if (payload.session_id !== pendingGame.sessionId) {
            return;
        }

        const sessionId = payload.session_id;
        const startedAt = pendingGame.startedAt;
        const finishedAt = moment.utc(startedAt).add(payload.time_played, 'seconds').format(dateTimeFormat);

        void this.upsertGameResult(payload, sessionId, startedAt, finishedAt);
    }

    private async upsertGameResult(
        payload: IncomingGameResultDeepLinkPayload,
        sessionId: string,
        startedAt: string,
        finishedAt: string
    ): Promise<void> {
        const amountOfPlayers = this.playerCount();
        const activityId = generateUuid();
        const upsertVenueGameActivityRequest: UpsertVenueGameActivityRequest = {
            activity_id: activityId,
            smart_device_guid: this.registeredOmni()?.id ? String(this.registeredOmni().id) : null,
            session_id: sessionId,
            game_id: null,
            winner_id: null,
            dartbot_level: null,
            mvp_user_id: null,
            mvp_score: null,
            game_mode: payload.game_mode,
            darts_thrown: payload.darts_thrown,
            game_started_at: startedAt,
            game_finished_at: finishedAt,
            cancelled_at: null,
            is_online: false,
            amount_of_players: amountOfPlayers,
        };

        try {
            await firstValueFrom(
                this.laneApiService.upsertVenueGameActivity({
                    upsertVenueGameActivityRequest,
                })
            );
            this.pendingGame.set(null);
        } catch (error) {
            this.alertService.errorFromApi(error);
        }
    }

    private openTargetArcade(payload: OutgoingTargetArcadeDeepLinkPayload): void {
        const url = this.deepLinkService.buildExternalAppUrl('targetArcade', payload);
        this.deepLinkService.openExternalUrl(url);
    }
}
